import { array } from '@ember/helper';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import { and, or, not } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  MaintenanceModeOfflineService,
  BreadcrumbsItem,
  LayoutSidebarSubnav,
} from 'core/components';

<template>
  {{#let
    (variation "hcp-ui-maintenance-mode-iam")
    @controller.model.isProjectContext
    (and (can "get service-principal") (can "list service-principal"))
    (or
      @controller.model.isProjectContext
      (and
        @controller.model.isProjectContext
        (can "get service-principal")
        (can "list service-principal")
      )
    )
    (not @controller.model.isProjectContext)
    (and
      (can "list invitations" undefined ssoEnabled=@controller.model.ssoEnabled)
    )
    (variation "hcp-ui-role-assignments-engine-organizations")
    (can "view role assignments in organization")
    (variation "hcp-ui-role-assignments-engine-projects")
    (can "view role assignments in project")
    as |isIamInMaintenanceMode isProjectContext canViewServicePrincipals shouldShowProjectIamSubnav shouldShowOrgIamSubnav canViewInvitations hasOrganizationRoleAssignmentsEnabled canViewRoleAssignmentsInOrganization hasProjectRoleAssignmentsEnabled canViewRoleAssignmentsInProject|
  }}
    {{#if isIamInMaintenanceMode}}
      <MaintenanceModeOfflineService
        @serviceName={{t
          "components.page.access-control.maintenance-mode.title"
        }}
      />
    {{else}}
      {{! Breadcrumbs below ... }}
      {{#if isProjectContext}}
        {{! Need to add the breadcrumb item here otherwise it wouldn't be picked up by breadcrumb container. Project is defined as a query param project_id and not as a route }}
        <BreadcrumbsItem
          @icon="file-text"
          @route="cloud.orgs.detail.projects.list"
          @models={{array @controller.model.organization.id}}
          @text={{t "components.page.projects.list.title-short"}}
        />
        <BreadcrumbsItem
          @icon="file-text"
          @route="cloud.orgs.detail.projects.detail.index"
          @models={{array
            @controller.model.organization.id
            @controller.model.project.id
          }}
          @text={{@controller.model.project.name}}
        />
      {{/if}}

      {{! Subnav below ... }}
      {{#if shouldShowProjectIamSubnav}}
        {{! NOTE: Everything in this else block is Project level ... }}
        {{#if hasProjectRoleAssignmentsEnabled}}
          <LayoutSidebarSubnav
            data-test-access-control-sidebar-subnav
            as |Subnav|
          >
            <Subnav.BackLink
              @text={{t "nav.back-to-cloud-dashboard"}}
              @route="cloud.orgs.detail.projects.detail.index"
              @models={{array
                @controller.model.organization.id
                @controller.model.project.id
              }}
              data-test-access-control-subnav-link-back
            />

            {{#if canViewRoleAssignmentsInProject}}
              <Subnav.Title
                data-test-access-control-subnav-title
                data-test-access-control-subnav-title-access-control
              >
                {{t "nav.project.access-control"}}
              </Subnav.Title>

              <Subnav.Link
                @icon="user-check"
                @text={{t "nav.project.role-assignments"}}
                @route="cloud.access-control.role-assignments"
                data-test-access-control-subnav-link-role-assignments
              />
            {{/if}}

            {{#if canViewServicePrincipals}}
              <Subnav.Title
                data-test-access-control-subnav-title
                data-test-access-control-subnav-title-access-control-manage
              >
                {{t "nav.project.manage"}}
              </Subnav.Title>

              <Subnav.Link
                @icon="robot"
                @text={{t
                  "components.page.access-control.service-principals.list.title"
                }}
                @route="cloud.access-control.service-principals"
                data-test-access-control-subnav-link-service-principals
              />
            {{/if}}
          </LayoutSidebarSubnav>
        {{else}}
          <LayoutSidebarSubnav
            data-test-access-control-sidebar-subnav
            as |Subnav|
          >
            <Subnav.BackLink
              @text={{t "nav.back-to-cloud-dashboard"}}
              @route="cloud.orgs.detail.projects.detail.index"
              @models={{array
                @controller.model.organization.id
                @controller.model.project.id
              }}
              data-test-access-control-subnav-link-back
            />

            <Subnav.Title data-test-access-control-subnav-title>
              {{t "nav.organization.settings.access-control"}}
            </Subnav.Title>

            <Subnav.Link
              @icon="user"
              @text={{t "components.page.access-control.users.list.title"}}
              @route="cloud.access-control.users"
              data-test-access-control-subnav-link-users
            />

            {{#if (can "get-iam-policy in project")}}
              <Subnav.Link
                @icon="users"
                @text={{t "components.page.access-control.groups.list.title"}}
                @route="cloud.access-control.groups"
                data-test-access-control-subnav-link-groups
              />
            {{/if}}

            {{#if canViewServicePrincipals}}
              <Subnav.Link
                @icon="robot"
                @text={{t
                  "components.page.access-control.service-principals.list.title"
                }}
                @route="cloud.access-control.service-principals"
                data-test-access-control-subnav-link-service-principals
              />
            {{/if}}
          </LayoutSidebarSubnav>
        {{/if}}
      {{else if shouldShowOrgIamSubnav}}
        {{! NOTE: Everything in this else block is Organization level ... }}
        {{#if hasOrganizationRoleAssignmentsEnabled}}
          <LayoutSidebarSubnav
            data-test-access-control-sidebar-subnav
            as |Subnav|
          >
            <Subnav.BackLink
              @text={{t "nav.back-to-cloud-dashboard"}}
              @route="cloud.orgs.detail.index"
              @models={{array @controller.model.organization.id}}
              data-test-access-control-subnav-link-back
            />

            {{#if canViewRoleAssignmentsInOrganization}}
              <Subnav.Title
                data-test-access-control-subnav-title
                data-test-access-control-subnav-title-access-control
              >
                {{t "nav.organization.settings.access-control"}}
              </Subnav.Title>

              <Subnav.Link
                @icon="user-check"
                @text={{t "nav.organization.role-assignments"}}
                @route="cloud.access-control.role-assignments"
                data-test-access-control-subnav-link-role-assignments
              />
            {{/if}}

            <Subnav.Title
              data-test-access-control-subnav-title
              data-test-access-control-subnav-title-access-control-manage
            >
              {{t "nav.organization.settings.manage"}}
            </Subnav.Title>

            {{#unless @controller.model.hasNoOrgRole}}
              <Subnav.Link
                @icon="user"
                @text={{t "components.page.access-control.users.list.title"}}
                @route="cloud.access-control.users"
                data-test-access-control-subnav-link-users
              />
            {{/unless}}

            {{#if canViewInvitations}}
              <Subnav.Link
                @icon="delay"
                @text={{t "components.page.access-control.invites.list.title"}}
                @route="cloud.access-control.invites"
                data-test-access-control-subnav-link-invites
              />
            {{/if}}

            {{! All Users can access Groups list view }}
            <Subnav.Link
              @icon="users"
              @text={{t "components.page.access-control.groups.list.title"}}
              @route="cloud.access-control.groups"
              data-test-access-control-subnav-link-groups
            />

            {{#if canViewServicePrincipals}}
              <Subnav.Link
                @icon="robot"
                @text={{t
                  "components.page.access-control.service-principals.list.title"
                }}
                @route="cloud.access-control.service-principals"
                data-test-access-control-subnav-link-service-principals
              />
            {{/if}}
          </LayoutSidebarSubnav>
        {{else}}
          <LayoutSidebarSubnav
            data-test-access-control-sidebar-subnav
            as |Subnav|
          >
            <Subnav.BackLink
              @text={{t "nav.back-to-cloud-dashboard"}}
              @route="cloud.orgs.detail.index"
              @models={{array @controller.model.organization.id}}
              data-test-access-control-subnav-link-back
            />

            <Subnav.Title data-test-access-control-subnav-title>
              {{t "nav.organization.settings.access-control"}}
            </Subnav.Title>
            {{#unless @controller.model.hasNoOrgRole}}
              <Subnav.Link
                @icon="user"
                @text={{t "components.page.access-control.users.list.title"}}
                @route="cloud.access-control.users"
                data-test-access-control-subnav-link-users
              />
            {{/unless}}
            {{! All Users can access Groups list view }}
            <Subnav.Link
              @icon="users"
              @text={{t "components.page.access-control.groups.list.title"}}
              @route="cloud.access-control.groups"
              data-test-access-control-subnav-link-groups
            />

            {{#if canViewInvitations}}
              <Subnav.Link
                @icon="delay"
                @text={{t "components.page.access-control.invites.list.title"}}
                @route="cloud.access-control.invites"
                data-test-access-control-subnav-link-invites
              />
            {{/if}}

            {{#if canViewServicePrincipals}}
              <Subnav.Link
                @icon="robot"
                @text={{t
                  "components.page.access-control.service-principals.list.title"
                }}
                @route="cloud.access-control.service-principals"
                data-test-access-control-subnav-link-service-principals
              />
            {{/if}}
          </LayoutSidebarSubnav>
        {{/if}}
      {{else}}
        {{! NOTE: Don't show a subnav otherwise ... }}
      {{/if}}

      {{outlet}}
    {{/if}}
  {{/let}}
</template>
